import {
  findOtherOption,
  formatChoiceWithOther,
  normalizeFieldOptions,
  readOtherText,
  type FieldDef,
} from '@citizen-walk/shared-types';

/**
 * 投稿モデレーション詳細 modal の「投稿者が入力した項目」を **読み取り専用**で表示する
 * 設定駆動セクション (admin)。
 *
 * 旧実装 (`PostsModerationDetail` の入力フィールド dl) は `post.extraFields` を生のまま列挙し、
 * 内部キーをそのままラベルに直書き・型整形なし・表示順/可視性無視というハードコードだった。
 * 本コンポーネントは地区の入力項目設定 (`District.dataSettings.postFields`) を唯一の source とし、
 * label / type / options / displayOrder / visible に基づいて表示する。
 *
 * apps/user 側の `PostDetailFields` (読み取り専用版) と同方針だが、eslint boundaries により
 * apps 間 import は不可のため、`@citizen-walk/shared-types` の共有ヘルパーを使って admin 版を
 * 最小再構築する (`PhotoPostFieldsForm` が `PostExtraFieldsForm` を再構築したのと同じ理由)。
 * admin モデレーションでは地図の ColorRule 着色は不要なため、user 版から着色ロジックは省く。
 *
 * 絞り込み規則 (`getRenderablePostFields` / user `PostDetailFields` と統一):
 *   1. `visible === false` を skip (詳細非表示にした項目は表示しない)
 *   2. `mode === 'hidden'` を skip (入力フォームに出さない項目 = 投稿者入力ではない)
 *   3. `EXCLUDED_POST_FIELD_KEYS` (本体 UI が扱う builtin) を skip
 *   4. `type === 'photo' / 'location'` を skip (写真・地図は別 UI で表示済み)
 *   5. 値が無い項目を skip、`displayOrder` 昇順 sort
 *
 * これにより `importedFrom` / `importedAt` / `aiGeneratedTitle` 等 (postFields に定義の無い
 * provenance / AI 派生キー) は自然に除外され、内部キーが UI に漏れない (memory: ui_no_internal_keys)。
 */

/** PostForm 本体が専用 UI で扱う builtin field key (user `EXCLUDED_POST_FIELD_KEYS` を逐語複製)。 */
const EXCLUDED_POST_FIELD_KEYS: ReadonlySet<string> = new Set([
  'title',
  'comment',
  'location',
  'photo',
  'detail',
]);

/** rating-scale の 0 が「未選択」を意味する (excludeZero) 場合などを考慮した「値あり」判定。 */
function hasValue(field: FieldDef, value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return false;
    if (field.type === 'rating-scale' && value === 0 && field.optionsMeta?.excludeZero === true) {
      return false;
    }
    return true;
  }
  // checkbox 等の boolean は「true (チェック済)」のみ表示 (false=未チェックはノイズ)。
  if (typeof value === 'boolean') return value === true;
  return Boolean(value);
}

/**
 * option の value → label を解決 (旧 string[] options も正規化、見つからなければ value をそのまま)。
 * 「その他」には記入内容 (`{key}__other`) を添える。
 */
function optionLabel(field: FieldDef, value: string, all: Record<string, unknown>): string {
  const options = normalizeFieldOptions(field.options);
  const label = options.find((o) => o.value === value)?.label ?? value;
  return findOtherOption({ ...field, options })?.value === value
    ? formatChoiceWithOther(label, readOtherText(all, field.key))
    : label;
}

/** 1 項目の値を型別に読み取り専用描画する (user `PostDetailFields` の FieldValue 相当、着色なし)。 */
function FieldValue({
  field,
  value,
  all,
}: {
  field: FieldDef;
  value: unknown;
  all: Record<string, unknown>;
}) {
  switch (field.type) {
    case 'select':
      return (
        <span className="text-sm text-slate-700">{optionLabel(field, String(value), all)}</span>
      );
    case 'multiselect': {
      const arr = Array.isArray(value) ? (value as unknown[]) : [];
      return (
        <div className="flex flex-wrap gap-1.5">
          {arr.map((raw, i) => (
            <span
              key={i}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-slate-200 text-slate-700"
            >
              {optionLabel(field, String(raw), all)}
            </span>
          ))}
        </div>
      );
    }
    case 'rating-scale': {
      const n = Number(value);
      return (
        <span className="text-sm font-semibold text-slate-700">{n > 0 ? `+${n}` : String(n)}</span>
      );
    }
    case 'number':
      return <span className="text-sm text-slate-700">{String(value)}</span>;
    case 'checkbox':
      return <span className="text-sm text-slate-700">はい</span>;
    case 'date':
      return <span className="text-sm text-slate-700">{String(value)}</span>;
    case 'text':
    case 'textarea':
    default:
      return (
        <p className="text-sm text-slate-700 whitespace-pre-wrap break-words leading-relaxed">
          {String(value)}
        </p>
      );
  }
}

export interface PostDetailFieldsViewProps {
  /** 地区 dataSettings.postFields (mode テンプレ default と merge 済を渡す)。 */
  postFields: FieldDef[];
  /** 投稿者入力値 (post.extraFields)。 */
  values: Record<string, unknown> | null | undefined;
}

export function PostDetailFieldsView({ postFields, values }: PostDetailFieldsViewProps) {
  const ef = values ?? {};
  const renderable = postFields
    .filter((f) => f.visible !== false)
    .filter((f) => f.mode !== 'hidden')
    .filter((f) => !EXCLUDED_POST_FIELD_KEYS.has(f.key))
    .filter((f) => f.type !== 'photo' && f.type !== 'location')
    .filter((f) => hasValue(f, ef[f.key]))
    .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));

  if (renderable.length === 0) return null;

  return (
    <section className="rounded border border-slate-200 p-3 grid grid-cols-1 gap-2">
      <h3 className="text-sm font-bold text-slate-700">入力項目</h3>
      <dl className="grid grid-cols-1 gap-2">
        {renderable.map((f) => (
          <div key={f.key}>
            <dt className="text-[11px] text-slate-500 mb-0.5">{f.label || f.key}</dt>
            <dd>
              <FieldValue field={f} value={ef[f.key]} all={ef} />
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
